import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Th,
  Thead,
  Tr,
  Tbody,
  Td,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  FormLabel,
  Input,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  AlertDescription,
  Center,
  Divider,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { Formik, Form } from 'formik';
import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { sortDataCellObjects } from './spreadsheet.utils';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import {
  useReportingMonacoEditor,
  REPORTING_XLSX_EDITOR_LANGUAGE,
} from 'packages/reporting/hooks/useReportingMonacoEditor';
import { FiPlusCircle } from 'react-icons/fi';

type DataCellObjectConfig = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObjectConfig;
type DataCellObject = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObject;
type EditDataCellObject = {
  dataCellObject: DataCellObject;
  updateParams: { isNew: true } | { isNew: false; idx: number };
};

type ValidateOutputType =
  | { isValid: true; data: DataCellObject[] }
  | { isValid: false; errors: string[] };

const EditDataCellObjectModal: React.FC<{
  editDataCellObject: EditDataCellObject;
  onSave: (validatedData: DataCellObject[]) => void;
  onValidate: (editDataCellObject: EditDataCellObject) => ValidateOutputType;
  onDelete: (idx: number) => void;
  onCancel: () => void;
  queryResults: CreditAgreement.Config.QueryResult[];
}> = ({
  onCancel,
  onSave,
  onValidate,
  onDelete,
  editDataCellObject,
  queryResults,
}) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useReportingMonacoEditor({ queryResults });

  const onClose = () => {
    setValidationErrors([]);
    onCancel();
  };

  const { updateParams } = editDataCellObject;

  return (
    <Modal isOpen={!!editDataCellObject} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent p="5" maxWidth="min-content" minWidth="xl">
        <ModalHeader>{updateParams.isNew ? 'Add' : 'Edit'} Cell</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="xl">
          <Formik
            onSubmit={(props) => {
              const validateResult = onValidate(props);
              if (!validateResult.isValid) {
                setValidationErrors(validateResult.errors);
                return;
              }
              onSave(validateResult.data);
              onClose();
            }}
            initialValues={editDataCellObject}
          >
            {(formik) => (
              <Form onSubmit={formik.handleSubmit}>
                <Box mb="7">
                  <FormLabel>Cell Address</FormLabel>
                  <Input
                    type="text"
                    name="dataCellObject.excelCoordinate"
                    value={formik.values.dataCellObject.excelCoordinate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Box>

                <Box mb="7">
                  <FormLabel mb="1">Cell Value</FormLabel>
                  <Text mb="1" fontSize="sm" fontStyle="italic">
                    Need starting &quot;=&quot; if want to denote as formula
                  </Text>
                  <Text mb="3" fontSize="sm" fontStyle="italic">
                    Remember to trigger saved inputs to be able to utilize
                    latest intellisense
                  </Text>
                  <Editor
                    language={REPORTING_XLSX_EDITOR_LANGUAGE}
                    value={String(formik.values.dataCellObject.value)}
                    onChange={(val) => {
                      formik.setFieldValue('dataCellObject.value', val);
                    }}
                    options={{
                      minimap: { enabled: false },
                      fixedOverflowWidgets: true,
                      suggest: { snippetsPreventQuickSuggestions: false },
                    }}
                    theme="vs-dark"
                    width="100%"
                    height="100px"
                  />
                </Box>

                {validationErrors.length > 0 && (
                  <Alert status="error" my="5" rounded="md">
                    <AlertIcon />
                    <AlertDescription>
                      Errors: {validationErrors.join(',')}
                    </AlertDescription>
                  </Alert>
                )}

                <Flex justifyContent="space-evenly">
                  <Button type="submit" w="28">
                    {updateParams.isNew ? 'Add' : 'Update'}
                  </Button>
                  {!updateParams.isNew && (
                    <Button
                      type="button"
                      w="28"
                      onClick={() => {
                        onDelete(updateParams.idx);
                        onClose();
                      }}
                    >
                      Delete
                    </Button>
                  )}
                  <Button type="button" w="28" onClick={onClose}>
                    Cancel
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const MultiDataCellEditModal: React.FC<{
  targetSheetConfig: DataCellObjectConfig;
  selectEditExcelCoords: Set<string>;
  isOpen: boolean;
  onSave: (validatedData: DataCellObject[]) => void;
  onCancel: () => void;
}> = ({
  selectEditExcelCoords,
  targetSheetConfig,
  isOpen,
  onSave,
  onCancel,
}) => {
  const [deleteAll, setDeleteAll] = useState<boolean>(false);
  const [rowAdjust, setRowAdjust] = useState<number>(0);
  const [colAdjust, setColAdjust] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const onClose = useCallback((): void => {
    setValidationErrors([]);
    onCancel();
  }, [onCancel]);

  const onSubmit = useCallback((): void => {
    let updatedDateConfigs: CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObject[];
    const errors: string[] = [];

    if (deleteAll) {
      // *** Delete All Selected ***
      updatedDateConfigs = targetSheetConfig.data.filter(
        ({ excelCoordinate }) => !selectEditExcelCoords.has(excelCoordinate),
      );
    } else {
      // *** Shift Selected Cell Addresses ***
      updatedDateConfigs = targetSheetConfig.data.map<DataCellObject>(
        (dateConfig) => {
          const { excelCoordinate, numberCoordinates } = dateConfig;
          if (!selectEditExcelCoords.has(excelCoordinate)) {
            return dateConfig;
          }

          const newNumCoords: CreditAgreement.Config.Reporting.Outputs.Xlsx.NumberCoordinates = {
            row: numberCoordinates.row + rowAdjust,
            column: numberCoordinates.column + colAdjust,
          };
          const newExcelCoords = commonUtils.numberCoordsToExcelCoords({
            r: newNumCoords.row,
            c: newNumCoords.column,
          });
          if (newNumCoords.row < 0) {
            errors.push(
              `Unable to adjust [rowAdjust:${rowAdjust}] for [cell:${excelCoordinate}]`,
            );
          }
          if (newNumCoords.column < 0) {
            errors.push(
              `Unable to adjust [colAdjust:${rowAdjust}] for [cell:${excelCoordinate}]`,
            );
          }

          return {
            ...dateConfig,
            numberCoordinates: newNumCoords,
            excelCoordinate: newExcelCoords,
          };
        },
      );
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    const validateErrors = reportingValidate.output.validateDataCellObjectConfig(
      { data: updatedDateConfigs },
    );
    if (validateErrors.length > 0) {
      setValidationErrors(validateErrors);
      return;
    }

    onSave(updatedDateConfigs);
    onClose();
  }, [
    colAdjust,
    deleteAll,
    onClose,
    onSave,
    rowAdjust,
    selectEditExcelCoords,
    targetSheetConfig.data,
  ]);

  return (
    <Modal
      isOpen={isOpen && selectEditExcelCoords.size > 0}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>
          Edit ({selectEditExcelCoords.size}) Multi Cells
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex mb="4" alignItems="center" justifyContent="space-between">
            <Text>Adjust Rows (+ / -)</Text>
            <NumberInput
              w="32"
              value={rowAdjust}
              onChange={(valStr) => setRowAdjust(parseInt(valStr, 10))}
              isDisabled={deleteAll}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Flex>

          <Flex alignItems="center" justifyContent="space-between">
            <Text>Adjust Columns (+ / -)</Text>
            <NumberInput
              w="32"
              value={colAdjust}
              onChange={(valStr) => setColAdjust(parseInt(valStr, 10))}
              isDisabled={deleteAll}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Flex>

          <Divider my="4" />

          <Flex alignItems="center" justifyContent="space-between">
            <Text>Delete All</Text>
            <Checkbox
              type="checkbox"
              isChecked={deleteAll}
              onChange={() => setDeleteAll((prev) => !prev)}
            />
          </Flex>

          {validationErrors.length > 0 && (
            <Alert status="error" rounded="md" mt="4">
              <AlertIcon />
              <AlertDescription>{validationErrors.join(',')}</AlertDescription>
            </Alert>
          )}

          <Flex justifyContent="space-evenly" mt="8">
            <Button type="button" w="28" onClick={onSubmit}>
              Save
            </Button>
            <Button type="button" w="28" onClick={onClose}>
              Cancel
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const ReportDataCellConfigView: React.FC<{
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: DataCellObjectConfig;
  queryResults: CreditAgreement.Config.QueryResult[];
}> = ({
  wipConfig,
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
  queryResults,
}) => {
  const [dataCellObjects, setDataCellObjects] = useState<DataCellObject[]>(
    targetSheetConfig.data,
  );
  const [editDataCell, setEditDataCell] = useState<EditDataCellObject | null>(
    null,
  );
  const [multiEditSelect, setMultiEditSelect] = useState<Set<string>>(
    new Set(),
  );
  const [isMultiEditModalOpen, setIsMultiEditModalOpen] = useState<boolean>(
    false,
  );

  useEffect(() => {
    setDataCellObjects(targetSheetConfig.data);
    setEditDataCell(null);
    setMultiEditSelect(new Set());
  }, [targetSheetConfig.data]);

  const addNewDataCell = (): void => {
    setEditDataCell({
      dataCellObject: {
        excelCoordinate: 'A1',
        numberCoordinates: { row: 0, column: 0 },
        value: '=placeholder',
      },
      updateParams: { isNew: true },
    });
  };

  const onValidate = useCallback(
    (editDataInput: EditDataCellObject): ValidateOutputType => {
      const { value, excelCoordinate } = editDataInput.dataCellObject;
      const { r, c } = commonUtils.excelCoordsToNumberCoords(excelCoordinate);
      const trueCellObject: DataCellObject = {
        value,
        excelCoordinate,
        numberCoordinates: { row: r, column: c },
      };

      const dataCellsCopy = _.cloneDeep(dataCellObjects);
      if (editDataInput.updateParams.isNew) {
        dataCellsCopy.push(trueCellObject);
      } else {
        dataCellsCopy[editDataInput.updateParams.idx] = trueCellObject;
      }

      const validateErrors = reportingValidate.output.validateDataCellObjectConfig(
        { data: dataCellsCopy },
      );
      if (validateErrors.length > 0) {
        return { isValid: false, errors: validateErrors };
      }

      return { isValid: true, data: dataCellsCopy };
    },
    [dataCellObjects],
  );

  const onSave = useCallback(
    (validatedData: DataCellObject[]): void => {
      if (!wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey)) {
        return;
      }

      const sortedCells = sortDataCellObjects(validatedData);
      const updatedDataCellConfig: DataCellObjectConfig = {
        data: sortedCells,
      };
      wipConfig.formProps.setFieldValue(
        sheetIdxConfigPropertyKey,
        updatedDataCellConfig,
      );
    },
    [sheetIdxConfigPropertyKey, wipConfig],
  );

  const onDelete = useCallback(
    (idx: number): void => {
      if (!wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey)) {
        return;
      }

      const updatedDataCells = dataCellObjects.filter(
        (_unused, loopIdx) => loopIdx !== idx,
      );
      const sortedCells = sortDataCellObjects(updatedDataCells);
      const updatedDataCellConfig: DataCellObjectConfig = {
        data: sortedCells,
      };
      wipConfig.formProps.setFieldValue(
        sheetIdxConfigPropertyKey,
        updatedDataCellConfig,
      );
    },
    [dataCellObjects, sheetIdxConfigPropertyKey, wipConfig],
  );

  return (
    <Box>
      {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) && (
        <Flex alignItems="center" mb="2">
          <Button
            type="button"
            px="2"
            onClick={() => addNewDataCell()}
            leftIcon={<FiPlusCircle />}
          >
            <Text ml="1">Add Cell Value</Text>
          </Button>

          <Center h="10" mx="6">
            <Divider orientation="vertical" />
          </Center>

          <Text mr="3">Multi Edit ({multiEditSelect.size}):</Text>

          <Button
            type="button"
            w="20"
            px="2"
            mr="2"
            onClick={() => setIsMultiEditModalOpen(true)}
            disabled={multiEditSelect.size === 0}
          >
            Edit
          </Button>

          <Button
            type="button"
            w="20"
            px="2"
            onClick={() => setMultiEditSelect(new Set())}
            disabled={multiEditSelect.size === 0}
          >
            Clear
          </Button>
        </Flex>
      )}

      {dataCellObjects.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) && (
                <Th textAlign="center">Edit</Th>
              )}
              <Th>Address</Th>
              <Th>Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            {dataCellObjects.map((dataCellObject, idx) => {
              const { excelCoordinate, value } = dataCellObject;
              const isMultiEditSelected = multiEditSelect.has(excelCoordinate);
              return (
                <Tr key={excelCoordinate}>
                  {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) && (
                    <Td w="28">
                      <Flex alignItems="center">
                        <EditIconButton
                          ariaLabel="edit cell"
                          label="Edit Cell"
                          mr="2"
                          onClick={() => {
                            setEditDataCell({
                              dataCellObject,
                              updateParams: { isNew: false, idx },
                            });
                          }}
                          disabled={multiEditSelect.size > 0}
                        />
                        <Checkbox
                          type="checkbox"
                          isChecked={isMultiEditSelected}
                          onChange={() => {
                            setMultiEditSelect((prev) => {
                              const newSet = new Set(prev);
                              if (isMultiEditSelected) {
                                newSet.delete(excelCoordinate);
                              } else {
                                newSet.add(excelCoordinate);
                              }
                              return newSet;
                            });
                          }}
                        />
                      </Flex>
                    </Td>
                  )}
                  <Td w="20">{excelCoordinate}</Td>
                  <Td>{String(value)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Text mt="3">No data configured currently.</Text>
      )}

      {editDataCell && (
        <EditDataCellObjectModal
          editDataCellObject={editDataCell}
          onSave={onSave}
          onValidate={onValidate}
          onDelete={onDelete}
          onCancel={() => {
            setEditDataCell(null);
          }}
          queryResults={queryResults}
        />
      )}

      {wipConfig?.isMutateAllowed(sheetIdxConfigPropertyKey) &&
        isMultiEditModalOpen && (
          <MultiDataCellEditModal
            targetSheetConfig={targetSheetConfig}
            isOpen={isMultiEditModalOpen}
            selectEditExcelCoords={multiEditSelect}
            onSave={onSave}
            onCancel={() => setIsMultiEditModalOpen(false)}
          />
        )}
    </Box>
  );
};
